import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { Button } from '../../../../flayr-components/Button/Button';
import { Flex } from '../../../../flayr-components/Flex/Flex';
import { NavBar } from '../../../../flayr-components/NavBar/NavBar';
import { ShowIf } from '../../../../flayr-components/ShowIf/ShowIf';
import useSteps from '../../../../hooks/useSteps';
import ArrayMap from '../../../../lib/ArrayMap/ArrayMap';
import Logo from '../../../../lib/Logo/Logo';
import Portal from '../../../../lib/Portal/Portal';
import SuccessMsg from '../../../../lib/SuccessMsg/SuccessMsg';
import { IInputField } from '../../useReviewFormPageData';
import ReviewFormItem from '../ReviewFormItem/ReviewFormItem';

interface ReviewFormWizardProps {
  type?: string;
  fields?: IInputField[];
  providerName?: string;
  customerName?: string;
  submitted?: string;
  onSave?: (e: any) => void;
  onSubmit?: (e: any) => void;
}

const ReviewFormWizard: React.FC<React.PropsWithChildren<ReviewFormWizardProps>> = ({
  type,
  fields = [],
  providerName,
  customerName,
  submitted,
  onSave,
  onSubmit,
}) => {
  const { index, isFirstStep, isLastStep, next, prev, setStep, setSteps } =
    useSteps<string>([]);

  const [stepValueMapping, setStepValueMapping] = React.useState<any>({});

  const [errors, setErrors] = React.useState<any>({});

  React.useEffect(() => {
    if (fields?.length) {
      const steps = fields.map((f) => f.name);
      setSteps(steps);
      let mapping = {};
      fields.map((f) => (mapping[f.name] = f.value));
      setStepValueMapping(mapping);
    }
  }, [fields]);

  function handleSave(e, skipNext = false) {
    let updating = 'reviewDimension';
    let valueField = 'score';
    let dataToPost: any = {};

    switch (e.name) {
      case 'publicFeedback':
        updating = e.name;
        valueField = 'comment';
        dataToPost = {
          updating,
          [valueField]: e.value || stepValueMapping[e.name],
        };
        break;
      default:
        dataToPost = {
          updating,
          [valueField]: e.value || stepValueMapping[e.name],
          id: e.name,
        };
        break;
    }
    const newMapping = {
      ...stepValueMapping,
      [e.name]: dataToPost[valueField],
    };
    if (e.isTrafficControl) {
      newMapping.trafficControl = e.value;
    }
    setStepValueMapping(newMapping);
    setTimeout(() => {
      onSave?.(dataToPost);
      if (!skipNext) {
        next();
      }
    }, 60);
  }

  function handleMediaChange(e) {
    setStepValueMapping({
      ...stepValueMapping,
      [e.name]: e.value,
    });
  }

  function handleSubmit() {
    const missingFields = fields.filter(
      (f) => f.isRequired && !stepValueMapping[f.name]
    );
    if (missingFields.length) {
      let errors: any = {};
      let missingKey = '';
      missingFields.forEach((field) => {
        if (!missingKey) {
          missingKey = field.name;
        }
        errors[field.name] = true;
      });
      setErrors(errors);
      setStep(missingKey);
      return;
    }

    onSubmit?.(stepValueMapping);
  }

  function handleInputChange(e) {
    setStepValueMapping({
      ...stepValueMapping,
      [e.name]: e.value,
    });
  }

  function handleNext() {
    const field = fields[index];
    const value = stepValueMapping[field.name];
    if (field.isRequired && !value) {
      const err = { [field.name]: true };
      return setErrors(err);
    }
    next();
  }

  return (
    <Portal
      target="body"
      className="fixed top-0 left-0 w-full h-full max-h-screen overflow-hidden bg-gray-100"
    >
      <ShowIf
        condition={!submitted}
        elseTemplate={
          <div className="p-4">
            <NavBar
              className="absolute top-0 left-0 w-full"
              title={<Logo />}
              shadow
            />
            <Flex center className="my-12">
              <div className="my-5 text-center text-green-500">
                <SuccessMsg message={submitted} />
              </div>
            </Flex>
          </div>
        }
      >
        <ArrayMap items={fields}>
          {(field, idx) => {
            return (
              <ReviewFormItem
                field={field}
                index={index}
                isScreenActive={index === idx}
                providerName={providerName}
                customerName={customerName}
                className={classNames(
                  'transition-transform duration-500 transform p-5',
                  {
                    'translate-none': index === idx,
                    'translate-y-full opacity-0': index < idx,
                    '-translate-y-full opacity-0': index > idx,
                  }
                )}
                isLastStep={isLastStep}
                value={stepValueMapping[field.name]}
                errors={errors}
                onNext={handleNext}
                onPrev={prev}
                onInputChange={handleInputChange}
                onSave={handleSave}
                onMediaChange={handleMediaChange}
                onSubmit={handleSubmit}
              />
            );
          }}
        </ArrayMap>
        <div className="min-h-screen"></div>
        <div className="sticky bottom-0 left-0 z-10 flex justify-end w-full p-2 bg-gray-300 bg-opacity-25 backdrop-blur-20">
          <Button
            className="w-10 h-10"
            shadow
            onClick={prev}
            disabled={isFirstStep}
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </Button>
          <Button
            className="w-10 h-10 ml-3"
            shadow
            onClick={handleNext}
            disabled={isLastStep}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
        </div>
      </ShowIf>
    </Portal>
  );
};
export default ReviewFormWizard;
